import { effectivePace, type PlayingPace } from '@league/shared/schemas';
import type { RemovalReview, RosterEntry, RosterRemovalResponse, RosterRemovalReviewResponse } from '@league/shared/contract';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { PLAYER_NAME, PLAYER_ROW, PlayerTable, QUIET_ACTION, Td, Th, ThActions, ThSelect } from '../../components/PlayerTable';
import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiPatch, apiPost } from '../../lib/api';
import { Facts, MobileList, MobileRow, pacePhrase, Sheet, SHEET_DANGER, SHEET_DONE, SHEET_LINK } from '../../components/MobileList';
import { PaceSelect } from './PaceSelect';
import {
  inviteAction,
  paceChoices,
  paceOverrideFor,
  removeManyNote,
  removeManyQuestion,
  selectedLabel,
} from './roster-status';

/** The authenticated app's pace names ("brisk" is shown as Quick). */
const PACE_NAMES = { relaxed: 'Relaxed', steady: 'Steady', brisk: 'Quick', unknown: 'Not set' } as const;

/**
 * THE ROSTER — owner, 2026-10-08. A plain table: a checkbox, the player's name (her page is where
 * her details are edited), her pace of play for this league as a dropdown on the row, and two
 * quiet text actions, Invite (or Resend invite) and Remove. Ticking rows turns the heading row into
 * a selection bar for inviting or removing several at once. Nothing else is on the row.
 *
 * THE WAITLIST IS THIS TABLE TOO — owner, 2026-10-16: "make it consistent". It adds the queue
 * position as a column and "Add to Roster" as a row action, and otherwise behaves the same.
 */
export function RosterTable({
  entries,
  leagueId,
  zone,
  selected,
  onSelect,
  onSelectAll,
  onInvite,
  onRemove,
  onChanged,
  waitlist,
}: {
  entries: RosterEntry[];
  leagueId: string;
  zone: string;
  selected: string[];
  onSelect: (enrollmentId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  /** The roster's existing invitation call, for one player or several. */
  onInvite: (playerIds: string[]) => Promise<unknown>;
  /** The roster's existing single-player confirmation. */
  onRemove: (entry: RosterEntry) => void;
  /** After a bulk removal: refresh what the page shows and clear the ticks. */
  onChanged: () => void;
  /** The waitlist's extras: the queue column and "Add to Roster", for one player or several. */
  waitlist?: { onPromote: (entry: RosterEntry) => Promise<unknown>; promoteDisabled: boolean; seatsLeft: number };
}) {
  /** Rows whose invitation went out while this page was open: they say so instead of offering it again. */
  const [sent, setSent] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState<Set<string>>(new Set());
  const [inviteError, setInviteError] = useState<string | null>(null);

  const invite = async (playerIds: string[]) => {
    if (playerIds.length === 0) return;
    setInviteError(null);
    setSending((previous) => new Set([...previous, ...playerIds]));
    try {
      await onInvite(playerIds);
      setSent((previous) => new Set([...previous, ...playerIds]));
    } catch (error) {
      setInviteError(error instanceof ApiError ? error.message : 'Could not send the invitation.');
    } finally {
      setSending((previous) => new Set([...previous].filter((id) => !playerIds.includes(id))));
    }
  };

  /* Several at once: the server first says who can go (scores, seating and the like protect some). */
  const confirmMany = useRef<HTMLDialogElement>(null);
  const [review, setReview] = useState<RemovalReview[] | null>(null);
  /** Anyone the removal could not take after all (Courtney's partial results, #59), said once. */
  const [notRemoved, setNotRemoved] = useState<string[]>([]);
  const inspect = useMutation({
    mutationFn: (ids: string[]) => apiPost<RosterRemovalReviewResponse>(`/api/leagues/${leagueId}/roster/removal-review`, { ids }),
    onSuccess: (data) => {
      setReview(data.rows);
      confirmMany.current?.showModal();
    },
  });
  const removeMany = useMutation({
    mutationFn: () =>
      apiPost<RosterRemovalResponse>(`/api/leagues/${leagueId}/roster/remove-selected`, {
        selection: (review ?? []).filter((row) => !row.reason).map(({ enrollmentId, revision }) => ({ enrollmentId, revision })),
      }),
    onSuccess: (data) => {
      const names = new Map((review ?? []).map((row) => [row.enrollmentId, row.name]));
      setNotRemoved(
        data.results
          .filter((result) => result.outcome !== 'removed')
          .map((result) => `${names.get(result.enrollmentId) ?? 'A player'} was not removed${result.reason ? `: ${result.reason}` : '.'}`),
      );
      confirmMany.current?.close();
      setReview(null);
      setSelecting(false);
      onChanged();
    },
  });
  const removable = (review ?? []).filter((row) => !row.reason);
  const kept = (review ?? []).filter((row) => row.reason);

  const queryClient = useQueryClient();
  /** The row whose panel is open on a phone. */
  const [openId, setOpenId] = useState<string | null>(null);
  /** Select mode on a phone: rows tick instead of opening, and a bar carries the group actions. */
  const [selecting, setSelecting] = useState(false);
  const opened = entries.find((entry) => entry.enrollment.id === openId) ?? null;

  const visibleIds = entries.map((entry) => entry.enrollment.id);
  const ticked = entries.filter((entry) => selected.includes(entry.enrollment.id));
  const allTicked = visibleIds.length > 0 && ticked.length === visibleIds.length;
  const toInvite = ticked.filter((entry) => inviteAction(entry, zone) !== null).map((entry) => entry.player.id);

  /* While the selection bar shows, the page gains room at its foot, so the bar never covers its end. */
  const barShowing = selecting && ticked.length > 0;
  useEffect(() => {
    if (!barShowing) return;
    document.body.classList.add('max-sm:pb-28');
    return () => document.body.classList.remove('max-sm:pb-28');
  }, [barShowing]);

  /*
    EVERYTHING ONE PLAYER'S PANEL CAN DO THAT MAKES SENSE FOR SEVERAL (owner, 2026-10-16): invite,
    set pace, add to the roster from the waitlist, remove. Only "View player profile" is one person's.
  */
  const [pacePicker, setPacePicker] = useState(false);
  const [groupNote, setGroupNote] = useState<string | null>(null);
  const groupPace = useMutation({
    mutationFn: (picked: PlayingPace | null) =>
      Promise.all(ticked.map((entry) => apiPatch(`/api/enrollments/${entry.enrollment.id}`, {
        paceOverride: picked === null ? null : paceOverrideFor(entry, picked),
      }))),
    onSuccess: async (_data, picked) => {
      await queryClient.invalidateQueries({ queryKey: ['roster', leagueId] });
      setPacePicker(false);
      const who = `${ticked.length} ${ticked.length === 1 ? 'player' : 'players'}`;
      setGroupNote(picked === null ? `Pace set back to each player’s own choice for ${who}.` : `Pace set to ${PACE_NAMES[picked]} for ${who}.`);
    },
  });
  const groupPromote = useMutation({
    mutationFn: async () => {
      if (!waitlist) return { added: 0, left: 0 };
      const inQueue = [...ticked].sort((a, b) => (a.enrollment.waitlistPosition ?? 0) - (b.enrollment.waitlistPosition ?? 0));
      let added = 0;
      for (const entry of inQueue.slice(0, Math.max(0, waitlist.seatsLeft))) {
        await waitlist.onPromote(entry);
        added += 1;
      }
      return { added, left: inQueue.length - added };
    },
    onSuccess: ({ added, left }) => {
      setGroupNote(`${added} added to the roster.${left > 0 ? ` ${left} could not be added: the league is full.` : ''}`);
      setSelecting(false);
      onChanged();
    },
  });
  const groupButtons = (size: 'inline' | 'app') => (<>
    <Button variant="secondary" size={size} disabled={toInvite.length === 0 || toInvite.some((id) => sending.has(id))} onClick={() => void invite(toInvite)}>
      Send invite
    </Button>
    <Button variant="secondary" size={size} onClick={() => { groupPace.reset(); setPacePicker(true); }}>
      Set pace
    </Button>
    {waitlist ? (
      <Button variant="secondary" size={size} disabled={waitlist.seatsLeft <= 0 || groupPromote.isPending} onClick={() => groupPromote.mutate()}>
        Add to Roster
      </Button>
    ) : null}
    <Button variant="destructive" size={size} disabled={inspect.isPending} onClick={() => { removeMany.reset(); setNotRemoved([]); inspect.mutate(ticked.map((entry) => entry.enrollment.id)); }}>
      {size === 'app' ? 'Remove' : 'Remove from league'}
    </Button>
  </>);

  const quiet = QUIET_ACTION;

  return (
    <div className="flex flex-col gap-2">
      <PlayerTable
        head={<>
              <ThSelect>
                <input
                  type="checkbox"
                  className="block h-5 w-5 accent-deep focus-visible:outline-2 focus-visible:outline-offset-4"
                  aria-label="Select all players"
                  checked={allTicked}
                  disabled={visibleIds.length === 0}
                  onChange={(event) => onSelectAll(event.target.checked)}
                />
              </ThSelect>
              {ticked.length > 0 ? (
                <th scope="col" colSpan={waitlist ? 4 : 3} className="pr-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span role="status" className="px-3 text-[14px] font-semibold text-ink">{selectedLabel(ticked.length)}</span>
                    <span className="flex flex-wrap items-center gap-2">{groupButtons('inline')}</span>
                  </div>
                </th>
              ) : (
                <>
                  {waitlist ? <Th>Queue</Th> : null}
                  <Th>Player</Th>
                  <Th>Pace of play</Th>
                  <ThActions />
                </>
              )}
        </>}
      >
            {entries.map((entry) => {
              const name = `${entry.player.firstName} ${entry.player.lastName}`;
              const isTicked = selected.includes(entry.enrollment.id);
              const action = inviteAction(entry, zone);
              return (
                <tr
                  key={entry.enrollment.id}
                  className={[
                    PLAYER_ROW,
                    isTicked ? 'bg-tint-deep hover:brightness-[0.97]' : 'hover:bg-chalk',
                  ].join(' ')}
                >
                  <Td kind="select">
                    <input
                      type="checkbox"
                      className="block h-5 w-5 accent-deep focus-visible:outline-2 focus-visible:outline-offset-4"
                      aria-label={`Select ${name}`}
                      checked={isTicked}
                      onChange={(event) => onSelect(entry.enrollment.id, event.target.checked)}
                    />
                  </Td>
                  {waitlist ? <Td className="tabular text-[14px] text-ink-2">{entry.enrollment.waitlistPosition}</Td> : null}
                  <Td kind="name">
                    <Link
                      to={`/director/players/${entry.player.id}`}
                      className={PLAYER_NAME}
                    >
                      {name}
                    </Link>
                    {/* A bad address — the director needs to know (Courtney, 2026-10-12). */}
                    {entry.player.emailBouncedAt ? <span className="block text-[13px] text-rust">Email bounced</span> : null}
                  </Td>
                  <Td className="whitespace-nowrap sm:pl-1">
                    <PaceSelect entry={entry} leagueId={leagueId} />
                  </Td>
                  <Td kind="actions">
                    <div className="flex items-center justify-end gap-1">
                      {/*
                        The invite slot always keeps the width of its widest label, so "Invite" turning
                        into "Invite sent" never moves a column (owner, 2026-10-08). No fixed column
                        widths: the table still sizes to its content on a phone.
                      */}
                      <span className="flex min-w-[7.5rem] justify-end">
                      {sent.has(entry.player.id) ? (
                        <span role="status" className="px-2.5 text-[14px] text-sage">Invite sent</span>
                      ) : action ? (
                        <button
                          type="button"
                          className={`${quiet} hover:border-deep`}
                          disabled={sending.has(entry.player.id)}
                          aria-label={`${action === 'resend' ? 'Resend invite to' : 'Invite'} ${name}`}
                          onClick={() => void invite([entry.player.id])}
                        >
                          {action === 'resend' ? 'Resend invite' : 'Invite'}
                        </button>
                      ) : null}
                      </span>
                      {waitlist ? (
                        <button
                          type="button"
                          className={`${quiet} hover:border-deep disabled:cursor-not-allowed`}
                          disabled={waitlist.promoteDisabled}
                          onClick={() => void waitlist.onPromote(entry)}
                        >
                          Add to Roster
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={`${quiet} hover:border-rust/35 hover:text-rust`}
                        aria-label={`Remove ${name} from league`}
                        onClick={() => onRemove(entry)}
                      >
                        Remove
                      </button>
                    </div>
                  </Td>
                </tr>
              );
            })}
      </PlayerTable>

      <div className="flex items-center justify-between gap-3 sm:hidden">
        {selecting ? (
          <Button variant="secondary" size="inline" disabled={visibleIds.length === 0} onClick={() => onSelectAll(!allTicked)}>
            {allTicked ? 'Clear all' : 'Select all'}
          </Button>
        ) : <span />}
        <Button variant="secondary" size="inline" disabled={entries.length === 0} onClick={() => { if (selecting) onSelectAll(false); setSelecting(!selecting); }}>
          {selecting ? 'Cancel' : 'Select'}
        </Button>
      </div>

      <MobileList>
        {entries.map((entry) => {
          const current = effectivePace(entry.player.preferredPace ?? null, entry.enrollment.paceOverride ?? null);
          const pace = pacePhrase(paceChoices(entry).find((c) => c.value === current)?.label ?? '', current === 'unknown');
          return (
            <MobileRow
              key={entry.enrollment.id}
              firstName={entry.player.firstName}
              lastName={entry.player.lastName}
              onOpen={() => setOpenId(entry.enrollment.id)}
              select={selecting ? { checked: selected.includes(entry.enrollment.id), onChange: (checked) => onSelect(entry.enrollment.id, checked) } : undefined}
              lines={[
                waitlist ? `#${entry.enrollment.waitlistPosition} in the queue · ${pace}` : pace,
                entry.player.emailBouncedAt ? <span className="text-rust">Email bounced</span> : null,
              ]}
            />
          );
        })}
      </MobileList>

      {/* The group actions, held at the bottom of the screen while anything is ticked. */}
      {barShowing ? (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-light bg-paper px-4 pb-6 pt-3 shadow-[0_-8px_24px_rgba(0,0,0,0.12)] sm:hidden">
          <p role="status" className="mb-2.5 text-[15px] font-semibold text-ink">{selectedLabel(ticked.length)}</p>
          {/* "Remove" here: the bar already says how many are selected, and the confirmation names the league. */}
          <div className="grid grid-cols-2 gap-2.5">{groupButtons('app')}</div>
        </div>
      ) : null}

      {pacePicker ? (
        <Sheet
          open
          onClose={() => setPacePicker(false)}
          title="Set pace"
          subtitle={selectedLabel(ticked.length)}
          footer={<><span /><button type="button" className={SHEET_DONE} onClick={() => setPacePicker(false)}>Cancel</button></>}
        >
          <div className="flex flex-col gap-2">
            {(['relaxed', 'steady', 'brisk'] as const).map((value) => (
              <Button key={value} variant="secondary" className="w-full justify-start" disabled={groupPace.isPending} onClick={() => groupPace.mutate(value)}>{PACE_NAMES[value]}</Button>
            ))}
            <Button variant="secondary" className="w-full justify-start" disabled={groupPace.isPending} onClick={() => groupPace.mutate(null)}>Each player’s own choice</Button>
          </div>
          {groupPace.error ? <ErrorNote>{groupPace.error instanceof ApiError ? groupPace.error.message : 'Could not set the pace.'}</ErrorNote> : null}
        </Sheet>
      ) : null}
      {groupNote ? <p role="status" className="text-[13px] text-ink-2">{groupNote}</p> : null}
      {groupPromote.error ? <ErrorNote>{groupPromote.error instanceof ApiError ? groupPromote.error.message : 'Could not add everyone to the roster.'}</ErrorNote> : null}

      {opened ? (() => {
        const name = `${opened.player.firstName} ${opened.player.lastName}`;
        const action = inviteAction(opened, zone);
        return (
          <Sheet
            open
            onClose={() => setOpenId(null)}
            title={name}
            subtitle={<>{opened.player.email ?? 'No email'}{opened.player.emailBouncedAt ? <span className="text-rust"> · Email bounced</span> : null}</>}
            footer={<>
              <button type="button" className={SHEET_DANGER} onClick={() => { setOpenId(null); onRemove(opened); }}>Remove from league</button>
              <button type="button" className={SHEET_DONE} onClick={() => setOpenId(null)}>Done</button>
            </>}
          >
            {waitlist ? <Facts items={[{ label: 'Waitlist', value: `#${opened.enrollment.waitlistPosition} in the queue` }]} /> : null}
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] text-ink-3">Pace of play</span>
              <PaceSelect entry={opened} leagueId={leagueId} field />
            </label>
            <div className="flex flex-wrap items-center gap-3">
              {sent.has(opened.player.id) ? (
                <span role="status" className="text-[15px] text-sage">Invite sent</span>
              ) : action ? (
                <Button variant="secondary" disabled={sending.has(opened.player.id)} onClick={() => void invite([opened.player.id])}>
                  {action === 'resend' ? 'Resend invite' : 'Invite'}
                </Button>
              ) : null}
              {waitlist ? (
                <Button variant="secondary" disabled={waitlist.promoteDisabled} onClick={() => { setOpenId(null); void waitlist.onPromote(opened); }}>
                  Add to Roster
                </Button>
              ) : null}
            </div>
            <Link to={`/director/players/${opened.player.id}`} className={`self-start ${SHEET_LINK}`}>View player profile</Link>
          </Sheet>
        );
      })() : null}

      {notRemoved.length > 0 ? (
        <ul role="status" className="text-[13px] text-ink-2">
          {notRemoved.map((line) => <li key={line}>{line}</li>)}
        </ul>
      ) : null}
      {inviteError ? <ErrorNote>{inviteError}</ErrorNote> : null}
      {inspect.error ? <ErrorNote>{inspect.error instanceof ApiError ? inspect.error.message : 'Could not check those players.'}</ErrorNote> : null}

      <div className="flex flex-wrap items-baseline justify-between gap-2 text-[13px] text-ink-3">
        <span className="max-sm:hidden">Click a player’s name to edit their details.</span>
        <Link to={`/director/leagues/${leagueId}/removed-players`} className="text-ink-2 underline underline-offset-4 hover:text-ink">
          View removed players
        </Link>
      </div>

      <dialog
        ref={confirmMany}
        aria-labelledby="remove-many-title"
        className="m-auto w-[calc(100%-2rem)] max-w-md rounded-md border border-light bg-paper p-6 text-ink backdrop:bg-black/30"
        onCancel={(event) => {
          if (removeMany.isPending) event.preventDefault();
          else setReview(null);
        }}
      >
        <h4 id="remove-many-title" className="font-display text-xl">{removeManyQuestion(removable.length)}</h4>
        {removable.length > 0 ? <p className="mt-2 text-sm text-ink-2">{removeManyNote(removable.length)}</p> : null}
        {kept.length > 0 ? (
          <ul className="mt-3 text-sm text-ink-2">
            {kept.map((row) => (
              <li key={row.enrollmentId}>{row.name} stays: {row.reason}</li>
            ))}
          </ul>
        ) : null}
        {removeMany.error ? (
          <ErrorNote>{removeMany.error instanceof ApiError ? removeMany.error.message : 'Could not remove those players.'}</ErrorNote>
        ) : null}
        <div className="mt-5 flex gap-3">
          <Button autoFocus variant="secondary" disabled={removeMany.isPending} onClick={() => { confirmMany.current?.close(); setReview(null); }}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={removable.length === 0 || removeMany.isPending} onClick={() => removeMany.mutate()}>
            {removeMany.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </div>
      </dialog>
    </div>
  );
}
